import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useCurrency } from '../hooks/useCurrency';
import { Customer } from '../types';
import { customerService, CustomerFilters as ServiceFilters } from '../services/customerService';
import CustomerModal from '../components/Customer/CustomerModal';
import ConfirmationModal from '../components/ConfirmationModal';
import PaymentModal from '../components/PaymentModal';
import { 
  Users, 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Phone, 
  MapPin,
  CreditCard,
  UserCheck,
  UserX,
  Grid,
  List,
  Eye,
  DollarSign,
  AlertCircle
} from 'lucide-react';
import { customToast } from '../utils/customToast';
import { useTitle } from '../hooks/useTitle';

const CustomersPage: React.FC = () => {
  const { theme } = useTheme();
  const { user, isLoading: isAuthLoading } = useAuth();
  const { formatCurrency } = useCurrency();
  const [searchParams] = useSearchParams();
  
  // Set page title
  useTitle('Customers');
  
  // State management
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit' | 'view'>('create');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [customerModalLoading, setCustomerModalLoading] = useState(false);
  
  // Filter and pagination
  const [filters, setFilters] = useState<ServiceFilters>({
    search: searchParams.get('search') || '',
    sortBy: 'name',
    sortOrder: 'asc'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(12);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // UI States
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Bulk operations state
  const [selectedCustomers, setSelectedCustomers] = useState<number[]>([]);

  // Confirmation modal state
  const [confirmationModal, setConfirmationModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    type?: 'danger' | 'warning' | 'info';
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    type: 'danger'
  });

  // Payment modal state
  const [paymentModal, setPaymentModal] = useState({
    isOpen: false,
    customer: null as Customer | null
  });
  const [paymentModalLoading, setPaymentModalLoading] = useState(false);

  // Confirmation modal loading
  const [confirmationLoading, setConfirmationLoading] = useState(false);

  // Helper function to get customer type display text and colors
  const getCustomerTypeInfo = (type?: string) => {
    switch (type) {
      case 'vip':
        return {
          label: 'VIP',
          bgColor: theme === 'dark' ? 'bg-purple-900' : 'bg-purple-100',
          textColor: theme === 'dark' ? 'text-purple-200' : 'text-purple-700',
          borderColor: theme === 'dark' ? 'border-purple-800' : ''
        };
      case 'business':
        return {
          label: 'Business',
          bgColor: theme === 'dark' ? 'bg-blue-900' : 'bg-blue-100',
          textColor: theme === 'dark' ? 'text-blue-200' : 'text-blue-700',
          borderColor: theme === 'dark' ? 'border-blue-800' : ''
        };
      case 'wholesale':
        return {
          label: 'Wholesale',
          bgColor: theme === 'dark' ? 'bg-indigo-900' : 'bg-indigo-100',
          textColor: theme === 'dark' ? 'text-indigo-200' : 'text-indigo-700',
          borderColor: theme === 'dark' ? 'border-indigo-800' : ''
        };
      case 'regular':
        return {
          label: 'Regular',
          bgColor: theme === 'dark' ? 'bg-green-900' : 'bg-green-100',
          textColor: theme === 'dark' ? 'text-green-200' : 'text-green-700',
          borderColor: theme === 'dark' ? 'border-green-800' : ''
        };
      case 'home':
        return {
          label: 'Home',
          bgColor: theme === 'dark' ? 'bg-orange-900' : 'bg-orange-100',
          textColor: theme === 'dark' ? 'text-orange-200' : 'text-orange-700',
          borderColor: theme === 'dark' ? 'border-orange-800' : ''
        };
      default: // 'retail' or unknown
        return {
          label: 'Retail',
          bgColor: theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100',
          textColor: theme === 'dark' ? 'text-gray-300' : 'text-gray-600',
          borderColor: theme === 'dark' ? 'border-gray-600' : ''
        };
    }
  };

  // Load customers
  const loadCustomers = useCallback(async () => {
    // Don't load data if auth is still loading or user is not authenticated
    if (isAuthLoading || !user) return;
    
    try {
      setLoading(true);
      setError(null);
      
      console.log('Loading customers with filters:', filters, 'page:', currentPage, 'perPage:', perPage);
      const response = await customerService.getCustomers(filters, currentPage, perPage);
      
      setCustomers(response.data);
      setTotal(response.total);
      setTotalPages(response.last_page);
      
    } catch (err) {
      console.error('Customer loading error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to load customers';
      setError(errorMessage);
      setCustomers([]); // Ensure customers is always an array
      // Don't show error toast for auth issues
      if (!(err as any)?.response?.status || (err as any).response.status !== 401) {
        customToast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  }, [filters, currentPage, perPage, isAuthLoading, user]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  // Handle customer operations
  const handleCreateCustomer = () => {
    setSelectedCustomer(null);
    setModalMode('create');
    setCustomerModalLoading(false);
    setIsModalOpen(true);
  };

  const handleEditCustomer = (customer: Customer) => {
    // Open modal immediately and fetch full customer in background
    setSelectedCustomer(customer);
    setModalMode('edit');
    setIsModalOpen(true);
    setCustomerModalLoading(true);
    customerService.getCustomer(customer.id)
      .then(full => setSelectedCustomer(full))
      .catch(err => {
        console.error('Failed to load customer for edit', err);
        customToast.error('Failed to load customer details');
      })
      .finally(() => setCustomerModalLoading(false));
  };

  const handleViewCustomer = (customer: Customer) => {
    setSelectedCustomer(customer);
    setModalMode('view');
    setIsModalOpen(true);
    setCustomerModalLoading(true);
    customerService.getCustomer(customer.id)
      .then(full => setSelectedCustomer(full))
      .catch(err => {
        console.error('Failed to load customer for view', err);
        customToast.error('Failed to load customer details');
      })
      .finally(() => setCustomerModalLoading(false));
  };

  const handleDeleteCustomer = async (customer: Customer) => {
    setConfirmationModal({
      isOpen: true,
      title: 'Delete Customer',
      message: `Are you sure you want to delete ${customer.name}? This action cannot be undone.`,
      type: 'danger',
      onConfirm: async () => {
        setConfirmationLoading(true);
        try {
          await customerService.deleteCustomer(customer.id);
          customToast.success('Customer deleted successfully');
          loadCustomers();
          setConfirmationModal(prev => ({ ...prev, isOpen: false }));
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to delete customer';
          customToast.error(errorMessage);
        } finally {
          setConfirmationLoading(false);
        }
      }
    });
  };

  // Handle payment functions
  const handleMakePayment = (customer: Customer) => {
    // Open payment modal immediately and fetch latest customer data
    setPaymentModal({ isOpen: true, customer: null });
    setPaymentModalLoading(true);
    setTimeout(() => {
      // allow modal to render instantly then fetch
      customerService.getCustomer(customer.id)
        .then(full => setPaymentModal({ isOpen: true, customer: full }))
        .catch(err => {
          console.error('Failed to load customer for payment', err);
          customToast.error('Failed to load customer details');
          setPaymentModal({ isOpen: false, customer: null });
        })
        .finally(() => setPaymentModalLoading(false));
    }, 50);
  };

  const handlePaymentCompleted = () => {
    loadCustomers(); // Reload customers to update dues
    setPaymentModal({ isOpen: false, customer: null });
  };

  // Handle filters
  const handleFilterChange = (key: keyof ServiceFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setFilters({
      search: '',
      sortBy: 'name',
      sortOrder: 'asc'
    });
    setSearchQuery('');
    setCurrentPage(1);
  };

  // Handle search
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    handleFilterChange('search', value);
  };

  // Bulk operations handlers
  const handleSelectCustomer = (customerId: number) => {
    setSelectedCustomers(prev => {
      if (prev.includes(customerId)) {
        return prev.filter(id => id !== customerId);
      } else {
        return [...prev, customerId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedCustomers.length === customers.length) {
      setSelectedCustomers([]);
    } else {
      setSelectedCustomers(customers.map(c => c.id));
    }
  };

  const handleBulkStatusChange = (isActive: boolean) => {
    const count = selectedCustomers.length;
    const statusText = isActive ? 'activate' : 'deactivate';
    
    setConfirmationModal({
      isOpen: true,
      title: `${isActive ? 'Activate' : 'Deactivate'} Customers`,
      message: `Are you sure you want to ${statusText} ${count} customer${count > 1 ? 's' : ''}?`,
      type: 'info',
      onConfirm: async () => {
        setConfirmationLoading(true);
        try {
          await customerService.bulkUpdateStatus(selectedCustomers, isActive);
          customToast.success(`${count} customer${count > 1 ? 's' : ''} ${statusText}d successfully`);
          setSelectedCustomers([]);
          loadCustomers();
          setConfirmationModal(prev => ({ ...prev, isOpen: false }));
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : `Failed to ${statusText} customers`;
          customToast.error(errorMessage);
        } finally {
          setConfirmationLoading(false);
        }
      }
    });
  };

  const handleBulkDelete = () => {
    const count = selectedCustomers.length;
    setConfirmationModal({
      isOpen: true,
      title: 'Delete Customers',
      message: `Are you sure you want to delete ${count} customer${count > 1 ? 's' : ''}? This action cannot be undone.`,
      type: 'danger',
      onConfirm: async () => {
        setConfirmationLoading(true);
        try {
          await customerService.bulkDelete(selectedCustomers);
          customToast.success(`${count} customer${count > 1 ? 's' : ''} deleted successfully`);
          setSelectedCustomers([]);
          loadCustomers();
          setConfirmationModal(prev => ({ ...prev, isOpen: false }));
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to delete customers';
          customToast.error(errorMessage);
        } finally {
          setConfirmationLoading(false);
        }
      }
    });
  };

  // Render customer list row
  const renderCustomerListItem = (customer: Customer) => (
    <div
      key={customer.id}
      className={`p-3 sm:p-4 border-b last:border-b-0 transition-all duration-200 hover:bg-opacity-50 ${
        theme === 'dark'
          ? 'border-slate-700 hover:bg-slate-700'
          : 'border-gray-200 hover:bg-gray-50'
      } ${selectedCustomers.includes(customer.id) ? 
        theme === 'dark' ? 'bg-blue-900/20' : 'bg-blue-50' 
        : ''}`}
    >
      {/* Mobile Layout - Stack vertically */}
      <div className="flex flex-col space-y-3 sm:hidden">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            <input
              type="checkbox"
              checked={selectedCustomers.includes(customer.id)}
              onChange={() => handleSelectCustomer(customer.id)}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600 flex-shrink-0 mt-0.5"
            />
            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
              theme === 'dark'
                ? 'bg-blue-900/50 text-blue-300'
                : 'bg-blue-100 text-blue-600'
            }`}>
              <UserCheck size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className={`font-semibold text-sm truncate ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                {customer.name}
              </h3>
            </div>
          </div>
          <div className="flex items-center space-x-1 flex-shrink-0">
            <button
              onClick={() => handleViewCustomer(customer)}
              className="text-blue-600 hover:text-blue-800 transition-colors p-1 rounded"
              title="View Customer"
            >
              <Eye className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleEditCustomer(customer)}
              className="text-yellow-600 hover:text-yellow-800 transition-colors p-1 rounded"
              title="Edit Customer"
            >
              <Edit className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleDeleteCustomer(customer)}
              className="text-red-600 hover:text-red-800 transition-colors p-1 rounded"
              title="Delete Customer"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        {/* Badges row */}
        <div className="flex flex-wrap gap-1.5 pl-10">
          <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${
            customer.is_active
              ? theme === 'dark'
                ? 'bg-green-900 text-green-200 border border-green-800'
                : 'bg-green-100 text-green-700'
              : theme === 'dark'
                ? 'bg-gray-700 text-gray-300 border border-gray-600'
                : 'bg-gray-100 text-gray-600'
          }`}>
            {customer.is_active ? 'Active' : 'Inactive'}
          </span>
          {customer.customer_type && (() => {
            const typeInfo = getCustomerTypeInfo(customer.customer_type);
            return (
              <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${
                typeInfo.bgColor
              } ${typeInfo.textColor} ${typeInfo.borderColor ? `border ${typeInfo.borderColor}` : ''}`}>
                {typeInfo.label}
              </span>
            );
          })()}
          {(() => {
            const balance = typeof customer.balance_amount === 'string' 
              ? parseFloat(customer.balance_amount) || 0 
              : (customer.balance_amount || 0);
            return balance > 0 ? (
              <span className="text-xs px-2 py-0.5 rounded-full flex-shrink-0 bg-red-100 text-red-700 border border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800">
                Dues: {formatCurrency(balance)}
              </span>
            ) : (
              <span className="text-xs px-2 py-0.5 rounded-full flex-shrink-0 bg-green-100 text-green-700 border border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800">
                No Dues
              </span>
            );
          })()}
        </div>
        
        {/* Contact info */}
        <div className="flex flex-col space-y-1.5 pl-10">
          {customer.phone && (
            <div className="flex items-center space-x-1.5">
              <Phone size={12} className={`flex-shrink-0 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
              <span className={`text-xs truncate ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                {customer.phone}
              </span>
            </div>
          )}
          {customer.address && (
            <div className="flex items-center space-x-1.5">
              <MapPin size={12} className={`flex-shrink-0 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
              <span className={`text-xs truncate ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                {customer.address}
              </span>
            </div>
          )}
        </div>
        
        {/* Payment button if has dues */}
        {(() => {
          const balance = typeof customer.balance_amount === 'string' 
            ? parseFloat(customer.balance_amount) || 0 
            : (customer.balance_amount || 0);
          return balance > 0 && (
            <button
              onClick={() => handleMakePayment(customer)}
              className="ml-10 px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 transition-colors touch-manipulation flex items-center justify-center space-x-1.5 w-fit"
            >
              <DollarSign size={14} />
              <span>Pay Dues</span>
            </button>
          );
        })()}
      </div>

      {/* Desktop/Tablet Layout */}
      <div className="hidden sm:flex items-center justify-between gap-3">
        <div className="flex items-center space-x-3 flex-1 min-w-0">
          {/* Bulk selection checkbox */}
          <input
            type="checkbox"
            checked={selectedCustomers.includes(customer.id)}
            onChange={() => handleSelectCustomer(customer.id)}
            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600 flex-shrink-0"
          />
          
          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
            theme === 'dark'
              ? 'bg-blue-900/50 text-blue-300'
              : 'bg-blue-100 text-blue-600'
          }`}>
            <UserCheck size={18} />
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center flex-wrap gap-2 mb-1">
              <h3 className={`font-semibold text-base truncate ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                {customer.name}
              </h3>
              {/* Status Badge */}
              <span className={`text-xs px-2 py-1 rounded-full flex-shrink-0 ${
                customer.is_active
                  ? theme === 'dark'
                    ? 'bg-green-900 text-green-200 border border-green-800'
                    : 'bg-green-100 text-green-700'
                  : theme === 'dark'
                    ? 'bg-gray-700 text-gray-300 border border-gray-600'
                    : 'bg-gray-100 text-gray-600'
              }`}>
                {customer.is_active ? 'Active' : 'Inactive'}
              </span>
              {/* Customer Type Badge */}
              {customer.customer_type && (() => {
                const typeInfo = getCustomerTypeInfo(customer.customer_type);
                return (
                  <span className={`text-xs px-2 py-1 rounded-full flex-shrink-0 ${
                    typeInfo.bgColor
                  } ${typeInfo.textColor} ${typeInfo.borderColor ? `border ${typeInfo.borderColor}` : ''}`}>
                    {typeInfo.label}
                  </span>
                );
              })()}
              {/* Dues Badge */}
              {(() => {
                const balance = typeof customer.balance_amount === 'string' 
                  ? parseFloat(customer.balance_amount) || 0 
                  : (customer.balance_amount || 0);
                return balance > 0 && (
                  <span className="text-xs px-2 py-1 rounded-full flex-shrink-0 bg-red-100 text-red-700 border border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800">
                    Dues: {formatCurrency(balance)}
                  </span>
                );
              })()}
            </div>
            
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              {customer.phone && (
                <div className="flex items-center space-x-1">
                  <Phone size={12} className="text-gray-400 flex-shrink-0" />
                  <span className={`text-sm truncate ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    {customer.phone}
                  </span>
                </div>
              )}
              {customer.address && (
                <div className="flex items-center space-x-1 min-w-0">
                  <MapPin size={12} className="text-gray-400 flex-shrink-0" />
                  <span className={`text-sm truncate ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    {customer.address}
                  </span>
                </div>
              )}
              {/* Account Status */}
              {(() => {
                const balance = typeof customer.balance_amount === 'string' 
                  ? parseFloat(customer.balance_amount) || 0 
                  : (customer.balance_amount || 0);
                return balance === 0 && (
                  <div className="flex items-center space-x-1">
                    <CreditCard size={12} className="text-green-500 flex-shrink-0" />
                    <span className="text-sm font-medium text-green-600">
                      No Dues
                    </span>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-1 flex-shrink-0">
          <button
            onClick={() => handleViewCustomer(customer)}
            className="text-blue-600 hover:text-blue-800 transition-colors p-1 rounded"
            title="View Customer"
          >
            <Eye className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleEditCustomer(customer)}
            className="text-yellow-600 hover:text-yellow-800 transition-colors p-1 rounded"
            title="Edit Customer"
          >
            <Edit className="w-4 h-4" />
          </button>
          
          {/* Payment button - only show if customer has dues */}
          {(() => {
            const balance = typeof customer.balance_amount === 'string' 
              ? parseFloat(customer.balance_amount) || 0 
              : (customer.balance_amount || 0);
            return balance > 0 && (
              <button
                onClick={() => handleMakePayment(customer)}
                className="text-green-600 hover:text-green-800 transition-colors p-1 rounded"
                title="Process Payment"
              >
                <DollarSign className="w-4 h-4" />
              </button>
            );
          })()}
          
          <button
            onClick={() => handleDeleteCustomer(customer)}
            className="text-red-600 hover:text-red-800 transition-colors p-1 rounded"
            title="Delete Customer"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );

  // Render customer card
  const renderCustomerCard = (customer: Customer) => (
    <div
      key={customer.id}
      className={`p-4 sm:p-6 rounded-xl sm:rounded-2xl border transition-all duration-200 hover:shadow-lg ${
        theme === 'dark'
          ? 'bg-slate-800 border-slate-700 hover:border-slate-600 hover:bg-slate-750'
          : 'bg-white border-gray-200 hover:border-gray-300 hover:bg-gray-50'
      } ${selectedCustomers.includes(customer.id) ? 
        theme === 'dark' ? 'ring-2 ring-blue-500 bg-blue-900/20' : 'ring-2 ring-blue-500 bg-blue-50' 
        : ''}`}
    >
      {/* Customer Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3 min-w-0 flex-1">
          {/* Bulk selection checkbox */}
          <input
            type="checkbox"
            checked={selectedCustomers.includes(customer.id)}
            onChange={() => handleSelectCustomer(customer.id)}
            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600 flex-shrink-0"
          />
          
          <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
            theme === 'dark'
              ? 'bg-blue-900/50 text-blue-300'
              : 'bg-blue-100 text-blue-600'
          }`}>
            <UserCheck size={18} className="sm:w-5 sm:h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center space-x-2 mb-1">
              <h3 className={`text-base sm:text-lg font-semibold truncate ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                {customer.name}
              </h3>
              {/* Status Badge */}
              <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${
                customer.is_active
                  ? theme === 'dark'
                    ? 'bg-green-900 text-green-200 border border-green-800'
                    : 'bg-green-100 text-green-700'
                  : theme === 'dark'
                    ? 'bg-gray-700 text-gray-300 border border-gray-600'
                    : 'bg-gray-100 text-gray-600'
              }`}>
                {customer.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <p className={`text-xs sm:text-sm ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
            }`}>
              Registered Customer
            </p>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="flex items-center space-x-1 flex-shrink-0">
          <button
            onClick={() => handleViewCustomer(customer)}
            className="text-blue-600 hover:text-blue-800 transition-colors p-1 rounded"
            title="View Customer"
          >
            <Eye className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleEditCustomer(customer)}
            className="text-yellow-600 hover:text-yellow-800 transition-colors p-1 rounded"
            title="Edit Customer"
          >
            <Edit className="w-4 h-4" />
          </button>
          
          {/* Payment button - only show if customer has dues */}
          {(() => {
            const balance = typeof customer.balance_amount === 'string' 
              ? parseFloat(customer.balance_amount) || 0 
              : (customer.balance_amount || 0);
            return balance > 0 && (
              <button
                onClick={() => handleMakePayment(customer)}
                className="text-green-600 hover:text-green-800 transition-colors p-1 rounded"
                title="Process Payment"
              >
                <DollarSign className="w-4 h-4" />
              </button>
            );
          })()}
          
          <button
            onClick={() => handleDeleteCustomer(customer)}
            className="text-red-600 hover:text-red-800 transition-colors p-1 rounded"
            title="Delete Customer"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Customer Details */}
      <div className="space-y-3">
        {/* Contact Information */}
        {customer.phone && (
          <div className="flex items-center space-x-2">
            <Phone size={14} className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} flex-shrink-0`} />
            <span className={`text-sm truncate ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}>
              {customer.phone}
            </span>
          </div>
        )}
        
        {customer.address && (
          <div className="flex items-start space-x-2">
            <MapPin size={14} className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} flex-shrink-0 mt-0.5`} />
            <span className={`text-sm ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}>
              {customer.address}
            </span>
          </div>
        )}

        {/* Dues Status Card - Prominently displayed */}
        <div className={`mt-4 p-3 rounded-lg border-l-4 ${
          (() => {
            const balance = typeof customer.balance_amount === 'string' 
              ? parseFloat(customer.balance_amount) || 0 
              : (customer.balance_amount || 0);
            return balance > 0 
              ? theme === 'dark'
                ? 'bg-red-900/20 border-red-500 border-red-500/50'
                : 'bg-red-50 border-red-500'
              : theme === 'dark'
                ? 'bg-green-900/20 border-green-500 border-green-500/50'
                : 'bg-green-50 border-green-500';
          })()
        }`}>
          {(() => {
            const balance = typeof customer.balance_amount === 'string' 
              ? parseFloat(customer.balance_amount) || 0 
              : (customer.balance_amount || 0);
            return balance > 0 ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <CreditCard size={16} className="text-red-500 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-red-600">Outstanding Dues</p>
                    <p className="text-lg font-bold text-red-700">
                      {formatCurrency(balance)}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleMakePayment(customer)}
                  className="px-3 py-1.5 bg-red-600 text-white text-xs font-medium rounded-lg hover:bg-red-700 transition-colors touch-manipulation"
                >
                  Pay Now
                </button>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <CreditCard size={16} className="text-green-500 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-green-600">Account Status</p>
                  <p className="text-sm font-semibold text-green-700">No Outstanding Dues</p>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-3 sm:space-y-4 lg:space-y-6">
      {/* Header */}
      <div className="flex flex-col space-y-3 sm:space-y-4">
        <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className={`text-xl sm:text-2xl lg:text-3xl font-bold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              Customers
            </h1>
            <p className={`mt-1 text-sm ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
              Manage walk-in and registered customers.
            </p>
          </div>
          
          <div className="flex flex-col space-y-2 sm:space-y-0 sm:flex-row sm:items-center sm:space-x-3">
            {/* View Mode Toggle */}
            <div className={`flex items-center border rounded-xl p-1 transition-all duration-200 ${
              theme === 'dark' ? 'border-gray-600 bg-gray-800' : 'border-gray-300 bg-white'
            }`}>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg transition-all duration-200 touch-manipulation ${
                  viewMode === 'grid'
                    ? 'bg-blue-500 text-white shadow-md'
                    : theme === 'dark'
                      ? 'text-gray-400 hover:text-white hover:bg-gray-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
                title="Grid View"
              >
                <Grid size={16} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg transition-all duration-200 touch-manipulation ${
                  viewMode === 'list'
                    ? 'bg-blue-500 text-white shadow-md'
                    : theme === 'dark'
                      ? 'text-gray-400 hover:text-white hover:bg-gray-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
                title="List View"
              >
                <List size={16} />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2 sm:flex sm:space-x-3 sm:gap-0">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center justify-center space-x-2 px-3 py-2 sm:px-4 rounded-xl border transition-all duration-200 shadow-sm hover:shadow-md touch-manipulation ${
                  theme === 'dark'
                    ? 'border-gray-600 text-gray-300 bg-gray-800 hover:bg-gray-700 hover:border-gray-500'
                    : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400'
                }`}
                title={showFilters ? 'Hide Filters' : 'Show Filters'}
              >
                <Filter size={16} />
                <span className="text-sm">Filters</span>
              </button>
              
              <button
                onClick={handleCreateCustomer}
                className="flex items-center justify-center space-x-2 px-3 py-2 sm:px-4 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-all duration-200 shadow-lg hover:shadow-xl font-medium touch-manipulation"
                title="Add New Customer"
              >
                <Plus size={16} />
                <span className="text-sm">Add Customer</span>
              </button>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative">
          <Search size={16} className={`absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
          }`} />
          <input
            type="text"
            placeholder="Search customers..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className={`w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg sm:rounded-xl border transition-all duration-200 shadow-sm focus:shadow-md touch-manipulation ${
              theme === 'dark'
                ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500 focus:bg-gray-750'
                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:bg-gray-50'
            } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20`}
          />
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className={`p-4 sm:p-6 rounded-xl sm:rounded-2xl border ${
          theme === 'dark'
            ? 'bg-slate-800 border-slate-700'
            : 'bg-white border-gray-200'
        }`}>
          <div className="flex flex-wrap gap-3 sm:gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className={`block text-xs sm:text-sm font-medium mb-2 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Customer Type
              </label>
              <select
                value={filters.customerType || 'all'}
                onChange={(e) => handleFilterChange('customerType', e.target.value)}
                className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg sm:rounded-xl border text-sm transition-colors touch-manipulation ${
                  theme === 'dark'
                    ? 'bg-slate-700 border-slate-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              >
                <option value="all">All Types</option>
                <option value="retail">Retail (Individual Customer)</option>
                <option value="home">Home (Personal/Home-based)</option>
                <option value="wholesale">Wholesale (Bulk Buyer)</option>
                <option value="business">Business (Registered Company)</option>
                <option value="regular">Regular (Frequent Customer)</option>
                <option value="vip">VIP (Premium Customer)</option>
              </select>
            </div>

            <div className="flex-1 min-w-[200px]">
              <label className={`block text-xs sm:text-sm font-medium mb-2 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Balance Status
              </label>
              <select
                value={filters.balanceStatus || 'all'}
                onChange={(e) => handleFilterChange('balanceStatus', e.target.value)}
                className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg sm:rounded-xl border text-sm transition-colors touch-manipulation ${
                  theme === 'dark'
                    ? 'bg-slate-700 border-slate-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              >
                <option value="all">All Balances</option>
                <option value="paid">No Balance (Fully Paid)</option>
                <option value="pending">Has Balance (Pending Payment)</option>
              </select>
            </div>

            <div className="flex-1 min-w-[200px]">
              <label className={`block text-xs sm:text-sm font-medium mb-2 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Status
              </label>
              <select
                value={filters.activeStatus || 'all'}
                onChange={(e) => handleFilterChange('activeStatus', e.target.value)}
                className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg sm:rounded-xl border text-sm transition-colors touch-manipulation ${
                  theme === 'dark'
                    ? 'bg-slate-700 border-slate-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              >
                <option value="all">All Statuses</option>
                <option value="active">Active Customers</option>
                <option value="inactive">Inactive Customers</option>
              </select>
            </div>

            <div className="flex-1 min-w-[200px]">
              <label className={`block text-xs sm:text-sm font-medium mb-2 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Sort By
              </label>
              <select
                value={filters.sortBy || 'name'}
                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg sm:rounded-xl border text-sm transition-colors touch-manipulation ${
                  theme === 'dark'
                    ? 'bg-slate-700 border-slate-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              >
                <option value="name">Sort by Name (A-Z)</option>
                <option value="created_at">Sort by Date Added (Newest)</option>
                <option value="balance_amount">Sort by Balance Amount</option>
              </select>
            </div>

            <button
              onClick={resetFilters}
              className={`px-4 py-2 sm:py-2.5 rounded-lg sm:rounded-xl border text-xs sm:text-sm transition-colors touch-manipulation whitespace-nowrap ${
                theme === 'dark'
                  ? 'border-slate-600 text-gray-300 hover:bg-slate-700'
                  : 'border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}
            >
              Reset
            </button>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 lg:gap-6">
        <div className={`p-3 sm:p-4 lg:p-6 rounded-xl sm:rounded-2xl border ${
          theme === 'dark'
            ? 'bg-slate-800 border-slate-700'
            : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center space-x-2 sm:space-x-3">
            <div className={`w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-full flex items-center justify-center ${
              theme === 'dark' 
                ? 'bg-blue-900/50 text-blue-400' 
                : 'bg-blue-100 text-blue-600'
            }`}>
              <Users className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6" />
            </div>
            <div className="min-w-0 flex-1">
              <p className={`text-xs sm:text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                Total
              </p>
              {loading ? (
                <div className={`h-6 sm:h-7 lg:h-8 rounded animate-pulse ${
                  theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                }`} />
              ) : (
                <p className={`text-lg sm:text-xl lg:text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} truncate`}>
                  {(total || 0).toLocaleString()}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className={`p-3 sm:p-4 lg:p-6 rounded-xl sm:rounded-2xl border ${
          theme === 'dark'
            ? 'bg-slate-800 border-slate-700'
            : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center space-x-2 sm:space-x-3">
            <div className={`w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-full flex items-center justify-center ${
              theme === 'dark' 
                ? 'bg-primary-900/50 text-primary-400' 
                : 'bg-primary-100 text-primary-600'
            }`}>
              <UserCheck className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6" />
            </div>
            <div className="min-w-0 flex-1">
              <p className={`text-xs sm:text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                Active
              </p>
              {loading ? (
                <div className={`h-6 sm:h-7 lg:h-8 rounded animate-pulse ${
                  theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                }`} />
              ) : (
                <p className={`text-lg sm:text-xl lg:text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} truncate`}>
                  {(customers || []).filter(c => c.is_active).length}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className={`p-3 sm:p-4 lg:p-6 rounded-xl sm:rounded-2xl border ${
          theme === 'dark'
            ? 'bg-slate-800 border-slate-700'
            : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center space-x-2 sm:space-x-3">
            <div className={`w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-full flex items-center justify-center ${
              theme === 'dark' 
                ? 'bg-slate-700/50 text-slate-300' 
                : 'bg-slate-100 text-slate-600'
            }`}>
              <UserX className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6" />
            </div>
            <div className="min-w-0 flex-1">
              <p className={`text-xs sm:text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                Inactive
              </p>
              {loading ? (
                <div className={`h-6 sm:h-7 lg:h-8 rounded animate-pulse ${
                  theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                }`} />
              ) : (
                <p className={`text-lg sm:text-xl lg:text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} truncate`}>
                  {(customers || []).filter(c => !c.is_active).length}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className={`p-3 sm:p-4 lg:p-6 rounded-xl sm:rounded-2xl border ${
          theme === 'dark'
            ? 'bg-slate-800 border-slate-700'
            : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center space-x-2 sm:space-x-3">
            <div className={`w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-full flex items-center justify-center ${
              theme === 'dark' 
                ? 'bg-red-900/50 text-red-400' 
                : 'bg-red-100 text-red-600'
            }`}>
              <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6" />
            </div>
            <div className="min-w-0 flex-1">
              <p className={`text-xs sm:text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                With Balance
              </p>
              {loading ? (
                <div className={`h-6 sm:h-7 lg:h-8 rounded animate-pulse ${
                  theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                }`} />
              ) : (
                <p className={`text-lg sm:text-xl lg:text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} truncate`}>
                  {(customers || []).filter(c => {
                    const balance = typeof c.balance_amount === 'string' 
                      ? parseFloat(c.balance_amount) || 0 
                      : (c.balance_amount || 0);
                    return balance > 0;
                  }).length}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className={`p-3 sm:p-4 lg:p-6 rounded-xl sm:rounded-2xl border col-span-2 lg:col-span-1 ${
          theme === 'dark'
            ? 'bg-slate-800 border-slate-700'
            : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center space-x-2 sm:space-x-3">
            <div className={`w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-full flex items-center justify-center ${
              theme === 'dark' 
                ? 'bg-orange-900/50 text-orange-400' 
                : 'bg-orange-100 text-orange-600'
            }`}>
              <CreditCard className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6" />
            </div>
            <div className="min-w-0 flex-1">
              <p className={`text-xs sm:text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                Total Dues
              </p>
              {loading ? (
                <div className={`h-6 sm:h-7 lg:h-8 rounded animate-pulse ${
                  theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                }`} />
              ) : (
                <div className="relative inline-block group">
                  <p className={`text-lg sm:text-xl lg:text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} truncate cursor-help`}>
                    {formatCurrency(
                      (customers || []).reduce((sum, c) => {
                        const balance = typeof c.balance_amount === 'string' 
                          ? parseFloat(c.balance_amount) || 0 
                          : (c.balance_amount || 0);
                        return sum + balance;
                      }, 0)
                    )}
                  </p>
                  {/* Tooltip */}
                  <div className={`absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 rounded-lg whitespace-nowrap text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 ${
                    theme === 'dark'
                      ? 'bg-slate-900 text-slate-100 border border-slate-700'
                      : 'bg-gray-900 text-white border border-gray-800'
                  }`}>
                    {formatCurrency(
                      (customers || []).reduce((sum, c) => {
                        const balance = typeof c.balance_amount === 'string' 
                          ? parseFloat(c.balance_amount) || 0 
                          : (c.balance_amount || 0);
                        return sum + balance;
                      }, 0)
                    )}
                    {/* Tooltip arrow */}
                    <div className={`absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent ${
                      theme === 'dark'
                        ? 'border-t-slate-900'
                        : 'border-t-gray-900'
                    }`} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Actions Toolbar */}
      {selectedCustomers.length > 0 && (
        <div className={`p-4 rounded-xl border ${
          theme === 'dark'
            ? 'bg-slate-800 border-slate-700'
            : 'bg-white border-gray-200'
        }`}>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center space-x-3">
              <span className={`text-sm font-medium ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}>
                {selectedCustomers.length} customer{selectedCustomers.length !== 1 ? 's' : ''} selected
              </span>
              <button
                onClick={() => setSelectedCustomers([])}
                className={`text-sm px-3 py-1 rounded-lg transition-colors ${
                  theme === 'dark'
                    ? 'text-gray-400 hover:text-gray-300 hover:bg-slate-700'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
              >
                Clear selection
              </button>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleSelectAll}
                className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                  theme === 'dark'
                    ? 'border-slate-600 text-gray-300 hover:bg-slate-700'
                    : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {selectedCustomers.length === customers?.length ? 'Deselect All' : 'Select All'}
              </button>
              <select
                onChange={(e) => {
                  if (e.target.value) {
                    handleBulkStatusChange(e.target.value === 'true');
                    e.target.value = '';
                  }
                }}
                className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                  theme === 'dark'
                    ? 'border-slate-600 bg-slate-700 text-gray-300'
                    : 'border-gray-300 bg-white text-gray-600'
                }`}
              >
                <option value="">Change Status</option>
                <option value="true">Set Active</option>
                <option value="false">Set Inactive</option>
              </select>
              <button
                onClick={handleBulkDelete}
                className="px-3 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-1"
              >
                <Trash2 className="w-4 h-4" />
                <span>Delete Selected</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Customer Display */}
      {loading ? (
        <>
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {Array.from({ length: 6 }).map((_, index) => (
                <div
                  key={index}
                  className={`p-4 sm:p-6 rounded-xl sm:rounded-2xl border animate-pulse ${
                    theme === 'dark'
                      ? 'bg-slate-800 border-slate-700'
                      : 'bg-white border-gray-200'
                  }`}
                >
                  {/* Header with checkbox, avatar, name + status badge, action buttons */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3 min-w-0 flex-1">
                      {/* Checkbox */}
                      <div className={`w-4 h-4 rounded flex-shrink-0 ${
                        theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                      }`} />
                      
                      {/* Avatar */}
                      <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex-shrink-0 ${
                        theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                      }`} />
                      
                      <div className="min-w-0 flex-1 space-y-2">
                        {/* Name + status badge row skeleton */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className={`h-5 w-24 rounded ${
                            theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                          }`} />
                          <div className={`h-5 w-16 rounded-full ${
                            theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                          }`} />
                        </div>
                        {/* Subtitle skeleton */}
                        <div className={`h-3 w-28 rounded ${
                          theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                        }`} />
                      </div>
                    </div>
                    
                    {/* Action buttons (3: view, edit, delete) */}
                    <div className="flex items-center space-x-1 flex-shrink-0">
                      {Array.from({ length: 3 }).map((_, idx) => (
                        <div
                          key={idx}
                          className={`w-8 h-8 rounded ${
                            theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                          }`}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Contact Info Row 1: Phone */}
                  <div className="flex items-center space-x-2 mb-2">
                    <div className={`w-4 h-4 rounded flex-shrink-0 ${
                      theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                    }`} />
                    <div className={`h-3 flex-1 rounded ${
                      theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                    }`} />
                  </div>
                  
                  {/* Contact Info Row 2: Address */}
                  <div className="flex items-start space-x-2 mb-4">
                    <div className={`w-4 h-4 rounded flex-shrink-0 mt-0.5 ${
                      theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                    }`} />
                    <div className={`h-3 flex-1 rounded ${
                      theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                    }`} />
                  </div>

                  {/* Dues Status Card - Prominently displayed */}
                  <div className={`p-3 rounded-lg border-l-4 ${
                    theme === 'dark'
                      ? 'bg-slate-700 border-slate-600'
                      : 'bg-gray-100 border-gray-300'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2 flex-1 min-w-0">
                        {/* Icon skeleton */}
                        <div className={`w-4 h-4 rounded flex-shrink-0 ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                        <div className="space-y-1.5 flex-1">
                          {/* Label skeleton */}
                          <div className={`h-3 w-24 rounded ${
                            theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                          }`} />
                          {/* Amount skeleton */}
                          <div className={`h-4 w-28 rounded ${
                            theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                          }`} />
                        </div>
                      </div>
                      {/* Button skeleton */}
                      <div className={`h-7 w-16 rounded flex-shrink-0 ${
                        theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                      }`} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={`rounded-xl sm:rounded-2xl border ${
              theme === 'dark'
                ? 'bg-slate-800 border-slate-700'
                : 'bg-white border-gray-200'
            }`}>
              {Array.from({ length: 5 }).map((_, rowIndex) => (
                <div
                  key={rowIndex}
                  className={`p-3 sm:p-4 border-b last:border-b-0 transition-all duration-200 ${
                    theme === 'dark' ? 'border-slate-700' : 'border-gray-200'
                  }`}
                >
                  {/* Mobile Layout Skeleton */}
                  <div className="flex flex-col space-y-3 sm:hidden animate-pulse">
                    {/* Top row: checkbox, avatar, name, action buttons */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center space-x-2 flex-1 min-w-0">
                        {/* Checkbox */}
                        <div className={`w-4 h-4 rounded flex-shrink-0 mt-0.5 ${
                          theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                        }`} />
                        
                        {/* Avatar */}
                        <div className={`w-8 h-8 rounded-full flex-shrink-0 ${
                          theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                        }`} />
                        
                        {/* Name skeleton */}
                        <div className={`h-4 flex-1 rounded ${
                          theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                        }`} />
                      </div>
                      
                      {/* Action buttons (3 buttons for mobile: view, edit, delete) */}
                      <div className="flex items-center space-x-1 flex-shrink-0">
                        {Array.from({ length: 3 }).map((_, idx) => (
                          <div
                            key={idx}
                            className={`w-6 h-6 rounded ${
                              theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                    
                    {/* Status badges row - 2 badges (Active, Dues) */}
                    <div className="flex flex-wrap gap-1.5 pl-10">
                      <div className={`h-5 w-12 rounded-full flex-shrink-0 ${
                        theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                      }`} />
                      <div className={`h-5 w-16 rounded-full flex-shrink-0 ${
                        theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                      }`} />
                    </div>
                    
                    {/* Contact info - phone and address (2 rows) */}
                    <div className="flex flex-col space-y-1.5 pl-10">
                      <div className="flex items-center space-x-1.5">
                        <div className={`w-3 h-3 flex-shrink-0 ${
                          theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                        }`} />
                        <div className={`h-3 flex-1 rounded ${
                          theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                        }`} />
                      </div>
                      <div className="flex items-center space-x-1.5">
                        <div className={`w-3 h-3 flex-shrink-0 ${
                          theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                        }`} />
                        <div className={`h-3 flex-1 rounded ${
                          theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                        }`} />
                      </div>
                    </div>
                    
                    {/* Payment button placeholder - only when has dues */}
                    <div className="ml-10 px-3 py-1.5">
                      <div className={`h-6 w-24 rounded-lg ${
                        theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                      }`} />
                    </div>
                  </div>

                  {/* Desktop/Tablet Layout Skeleton */}
                  <div className="hidden sm:flex items-center justify-between gap-3 animate-pulse">
                    {/* Left section: checkbox, avatar, details */}
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      {/* Checkbox */}
                      <div className={`w-4 h-4 rounded flex-shrink-0 ${
                        theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                      }`} />
                      
                      {/* Avatar */}
                      <div className={`w-10 h-10 rounded-full flex-shrink-0 ${
                        theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                      }`} />
                      
                      {/* Details section */}
                      <div className="flex-1 min-w-0 space-y-2.5">
                        {/* Name + badges row */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className={`h-5 w-32 rounded ${
                            theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                          }`} />
                          <div className={`h-5 w-16 rounded-full ${
                            theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                          }`} />
                          <div className={`h-5 w-20 rounded-full ${
                            theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                          }`} />
                        </div>
                        
                        {/* Contact info row with phone, address, dues status */}
                        <div className="flex flex-wrap items-center gap-x-6 gap-y-1.5">
                          {/* Phone */}
                          <div className="flex items-center space-x-1 min-w-0">
                            <div className={`w-3 h-3 rounded flex-shrink-0 ${
                              theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                            }`} />
                            <div className={`h-3 w-20 rounded ${
                              theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                            }`} />
                          </div>
                          
                          {/* Address */}
                          <div className="flex items-center space-x-1 min-w-0">
                            <div className={`w-3 h-3 rounded flex-shrink-0 ${
                              theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                            }`} />
                            <div className={`h-3 w-24 rounded ${
                              theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                            }`} />
                          </div>
                          
                          {/* No Dues indicator */}
                          <div className="flex items-center space-x-1">
                            <div className={`w-3 h-3 rounded flex-shrink-0 ${
                              theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                            }`} />
                            <div className={`h-3 w-16 rounded ${
                              theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                            }`} />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Right section: action buttons (4 buttons: view, edit, payment/optional, delete) */}
                    <div className="flex items-center space-x-1 flex-shrink-0">
                      {Array.from({ length: 4 }).map((_, idx) => (
                        <div
                          key={idx}
                          className={`w-8 h-8 rounded ${
                            theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      ) : error ? (
        <div className={`text-center py-8 sm:py-12 ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`}>
          <p className="text-sm sm:text-base">{error}</p>
          <button
            onClick={loadCustomers}
            className="mt-3 sm:mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg sm:rounded-xl hover:bg-blue-700 transition-colors touch-manipulation"
          >
            Retry
          </button>
        </div>
      ) : (customers || []).length === 0 ? (
        <div className={`text-center py-8 sm:py-12 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
          <Users className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 opacity-50" />
          <p className="text-base sm:text-lg font-medium">No customers found</p>
          <p className="mt-1 sm:mt-2 text-sm sm:text-base">Add your first customer to get started.</p>
        </div>
      ) : (
        <>
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {(customers || []).map(customer => renderCustomerCard(customer))}
            </div>
          ) : (
            <div className={`rounded-xl sm:rounded-2xl border ${
              theme === 'dark'
                ? 'bg-slate-800 border-slate-700'
                : 'bg-white border-gray-200'
            }`}>
              {(customers || []).map(customer => renderCustomerListItem(customer))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-0 pt-4 sm:pt-6">
              <div className={`text-sm order-2 sm:order-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Showing {((currentPage - 1) * perPage) + 1} to {Math.min(currentPage * perPage, total)} of {total} customers
              </div>
              <div className="flex items-center space-x-2 order-1 sm:order-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className={`px-3 sm:px-4 py-2 rounded-lg sm:rounded-xl border transition-colors touch-manipulation ${
                    currentPage === 1
                      ? theme === 'dark' 
                        ? 'border-slate-600 text-gray-500 cursor-not-allowed' 
                        : 'border-gray-200 text-gray-400 cursor-not-allowed'
                      : theme === 'dark'
                        ? 'border-slate-600 text-gray-300 hover:bg-slate-700'
                        : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <span className="hidden sm:inline">Previous</span>
                  <span className="sm:hidden">Prev</span>
                </button>
                <span className={`px-3 sm:px-4 py-2 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                  {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className={`px-3 sm:px-4 py-2 rounded-lg sm:rounded-xl border transition-colors touch-manipulation ${
                    currentPage === totalPages
                      ? theme === 'dark' 
                        ? 'border-slate-600 text-gray-500 cursor-not-allowed' 
                        : 'border-gray-200 text-gray-400 cursor-not-allowed'
                      : theme === 'dark'
                        ? 'border-slate-600 text-gray-300 hover:bg-slate-700'
                        : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Customer Modal */}
      <CustomerModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        mode={modalMode}
        customer={selectedCustomer}
        onSuccess={loadCustomers}
        isLoading={customerModalLoading}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={() => setConfirmationModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmationModal.onConfirm}
        title={confirmationModal.title}
        message={confirmationModal.message}
        type={confirmationModal.type}
        isLoading={confirmationLoading}
      />

      {/* Payment Modal */}
      <PaymentModal
        isOpen={paymentModal.isOpen}
        onClose={() => setPaymentModal({ isOpen: false, customer: null })}
        customer={paymentModal.customer}
        onPaymentCompleted={handlePaymentCompleted}
        isLoading={paymentModalLoading}
      />
    </div>
  );
};

export default CustomersPage;